import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, Clock, CheckCircle, FileText, Loader2, Eye, X, Upload } from 'lucide-react';
import { formatTL, hasInvoiceableAmount } from '../../utils/money';
import { apiService } from '../../services/api.service';
import { API_ENDPOINTS } from '../../config/environment';
import { getPaymentStatus, getFleetPaymentStatus, canUploadFleetInvoice } from '../../utils/paymentStatus';
import IssuedInvoices from './IssuedInvoices';
import './InvoicesPage.css';

/** @param embedded Hakedişler ekranında sekme olarak açıldığında kendi başlığını çizmez. */
const InvoicesPage = ({ onNavigate, embedded = false }) => {
  const [invoiceTab, setInvoiceTab] = useState('incoming'); // 'incoming' | 'outgoing' | 'issued'

  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [outgoing, setOutgoing] = useState([]);
  const [outgoingLoading, setOutgoingLoading] = useState(false);
  // "hiç yüklenmedi" ile "yüklendi ama 0 kayıt döndü" durumlarını ayırmak için ayrı bayrak —
  // yalnızca outgoing.length===0'a bakarsak sekmeye her geçişte API'ye yeniden istek atılır.
  // İstek başarısız olursa bayrak set edilmez ki kullanıcı sekmeyi değiştirip geri dönerek tekrar deneyebilsin.
  const [outgoingLoaded, setOutgoingLoaded] = useState(false);

  const loadOutgoing = useCallback(async () => {
    setOutgoingLoading(true);
    try {
      const res = await apiService.post(API_ENDPOINTS.FLEET_PANEL.FLEET_PAYMENTS_LIST, {
        page: 1, pageSize: 50, sortModels: [{ colId: 'PeriodEnd', sort: 'desc' }]
      });
      if (res.success) {
        setOutgoing(res.data?.items || []);
        setOutgoingLoaded(true);
      }
    } catch (error) {
      console.error('Failed to load fleet payments:', error);
    } finally {
      setOutgoingLoading(false);
    }
  }, []);

  useEffect(() => {
    if (invoiceTab === 'outgoing' && !outgoingLoaded) loadOutgoing();
  }, [invoiceTab, outgoingLoaded, loadOutgoing]);

  // Filo hakedişi sekmesine geç — App.jsx onNavigate geçmiyorsa hash routing'e düş.
  const goToFleetPayments = () => {
    if (onNavigate) onNavigate('fleet-payments');
    else window.location.hash = 'payouts:fleet';
  };

  useEffect(() => {
    loadInvoices();
    loadSummary();
  }, [page]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const response = await apiService.post(API_ENDPOINTS.FLEET_PANEL.INVOICES_LIST, {
        page,
        pageSize
      });

      if (response.success) {
        setInvoices(response.data?.items || []);
        const total = response.data?.totalCount || 0;
        setTotalPages(Math.max(1, Math.ceil(total / pageSize)));
      }
    } catch (error) {
      console.error('Failed to load invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      setSummaryLoading(true);
      const response = await apiService.get(API_ENDPOINTS.FLEET_PANEL.PAYMENT_SUMMARY);
      if (response.success) {
        setSummary(response.data);
      }
    } catch (error) {
      console.error('Failed to load payment summary:', error);
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleViewDetail = async (invoiceId) => {
    try {
      setDetailLoading(true);
      const response = await apiService.get(API_ENDPOINTS.FLEET_PANEL.INVOICE_DETAIL(invoiceId));
      if (response.success) {
        setSelectedInvoice(response.data);
      }
    } catch (error) {
      console.error('Failed to load invoice detail:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  // Etiket ve ton tek kaynaktan (utils/paymentStatus) — sayfaya özel harita tutmak,
  // enum 5 değere çıkınca "Onay Bekliyor"u "Onaylandı" gösteren kaymayı doğurmuştu.
  const getStatusBadge = (status) => {
    const st = getPaymentStatus(status);
    return <span className={`inv-status-badge ${st.tone}`}>{st.label}</span>;
  };

  const formatCurrency = (amount) => {
    if (amount == null) return '0 TL';
    return formatTL(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="fi-container">
      {!embedded && (
        <div className="page-header">
          <div className="header-left">
            <div className="page-icon"><FileText size={20} /></div>
            <div className="page-title">
              <h1>Faturalar & Ödemeler</h1>
              <p>Filo gelir ve ödeme takibi</p>
            </div>
          </div>
        </div>
      )}

      <div className="inv-tabs">
        <button
          type="button"
          className={`inv-tab ${invoiceTab === 'incoming' ? 'active' : ''}`}
          onClick={() => setInvoiceTab('incoming')}
        >
          Kuryelerden gelen
        </button>
        <button
          type="button"
          className={`inv-tab ${invoiceTab === 'outgoing' ? 'active' : ''}`}
          onClick={() => setInvoiceTab('outgoing')}
        >
          Hızlıyo'ya kestiğim
        </button>
        <button
          type="button"
          className={`inv-tab ${invoiceTab === 'issued' ? 'active' : ''}`}
          onClick={() => setInvoiceTab('issued')}
        >
          Hızlıyo'nun bana kestiği
        </button>
      </div>

      {invoiceTab === 'issued' && <IssuedInvoices />}

      {invoiceTab === 'issued' ? null : summaryLoading ? (
        <div className="fi-summary-loading">
          <Loader2 className="spinning" size={20} />
        </div>
      ) : summary && (
        <div className="fi-summary-grid">
          <div className="fi-summary-card">
            <div className="fi-summary-icon fi-icon-total">
              <TrendingUp size={20} />
            </div>
            <div className="fi-summary-content">
              <div className="fi-summary-label">Toplam Kazanç</div>
              <div className="fi-summary-value">{formatCurrency(summary.totalEarnings)}</div>
            </div>
          </div>

          <div className="fi-summary-card">
            <div className="fi-summary-icon fi-icon-pending">
              <Clock size={20} />
            </div>
            <div className="fi-summary-content">
              <div className="fi-summary-label">Bekleyen Ödemeler</div>
              <div className="fi-summary-value">{formatCurrency(summary.pendingPayments)}</div>
            </div>
          </div>

          <div className="fi-summary-card">
            <div className="fi-summary-icon fi-icon-paid">
              <CheckCircle size={20} />
            </div>
            <div className="fi-summary-content">
              <div className="fi-summary-label">Ödenen</div>
              <div className="fi-summary-value">{formatCurrency(summary.paidPayments)}</div>
            </div>
          </div>

          <div className="fi-summary-card">
            <div className="fi-summary-icon fi-icon-count">
              <FileText size={20} />
            </div>
            <div className="fi-summary-content">
              <div className="fi-summary-label">Fatura Sayısı</div>
              <div className="fi-summary-value">{summary.totalInvoiceCount || 0}</div>
            </div>
          </div>
        </div>
      )}

      {invoiceTab === 'incoming' && (loading ? (
        <div className="page-loading">
          <Loader2 className="spinning" size={40} />
        </div>
      ) : invoices.length === 0 ? (
        <div className="page-empty">
          <FileText size={48} />
          <h3>Kuryelerinizden gelen fatura yok</h3>
          <p>
            Kurye, haftalık hakedişi oluştuktan sonra faturasını yükler; belge onaya düştüğünde
            burada listelenir. Hakediş kayıtları Pazartesi 08:00'de üretilir.
          </p>
        </div>
      ) : (
        <>
          <div className="data-table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Kurye</th>
                  <th>Dönem</th>
                  <th>Teslimat</th>
                  <th>Tutar</th>
                  <th>Durum</th>
                  <th>Tarih</th>
                  <th>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>
                      <div className="fi-courier-name">
                        {invoice.courierName || '-'}
                      </div>
                      {invoice.courierPhone && (
                        <div className="fi-courier-phone">{invoice.courierPhone}</div>
                      )}
                    </td>
                    <td>{invoice.period?.display || '-'}</td>
                    <td>{invoice.summary?.totalDeliveries ?? '-'}</td>
                    <td>
                      <div className="fi-amount">
                        {formatCurrency(invoice.summary?.totalEarnings)}
                      </div>
                    </td>
                    <td>{getStatusBadge(invoice.status)}</td>
                    <td>{formatDate(invoice.createdAtUtc)}</td>
                    <td>
                      <button
                        className="fi-detail-btn"
                        onClick={() => handleViewDetail(invoice.id)}
                        title="Detay"
                      >
                        <Eye size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="simple-pagination">
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Önceki
            </button>
            <span>
              Sayfa {page} / {totalPages}
            </span>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Sonraki
            </button>
          </div>
        </>
      ))}

      {invoiceTab === 'outgoing' && (
        outgoingLoading ? (
          <div className="page-loading">
            <Loader2 className="spinning" size={40} />
          </div>
        ) : outgoing.length === 0 ? (
          <div className="page-empty">
            <FileText size={48} />
            <h3>Fatura kesilecek hakediş yok</h3>
            <p>
              Filo hakedişiniz oluştuğunda burada listelenir ve faturasını yükleyebilirsiniz.
              Dönem Pazartesi 07:00'de kapanır; faturayı hakediş tutarıyla birebir aynı kesin.
            </p>
          </div>
        ) : (
          <div className="data-table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Dönem</th>
                  <th>Faturalanacak Tutar</th>
                  <th>Durum</th>
                  <th>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {outgoing.map((item) => {
                  const st = getFleetPaymentStatus(item.status);
                  return (
                    <tr key={item.id}>
                      <td>{item.periodRange || item.period || '-'}</td>
                      <td>
                        <div className="fi-amount">
                          {formatCurrency(item.totalFleetShareAmount)}
                        </div>
                      </td>
                      <td><span className={`inv-status-badge ${st.tone}`}>{st.label}</span></td>
                      <td>
                        {/* Faturalanacak tutar ≤ 0 ise fatura kesilemez; yönlendirme yerine
                            sebebi yazan kısa bilgi gösterilir (Filo Hakedişleri ile aynı kural). */}
                        {canUploadFleetInvoice(item.status) && (
                          hasInvoiceableAmount(item.totalFleetShareAmount) ? (
                            <button
                              type="button"
                              className="inv-upload-link"
                              onClick={goToFleetPayments}
                            >
                              <Upload size={14} />
                              Filo Hakedişleri'nde yükle
                            </button>
                          ) : (
                            <span
                              className="inv-no-invoice"
                              title="Bu dönemde faturalanacak tutar oluşmadı: zaman-bazlı kurye gideri paket gelirine eşit ya da ondan yüksek."
                            >
                              Fatura kesilmez
                            </span>
                          )
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {selectedInvoice && (
        <div className="modal-overlay" onClick={() => setSelectedInvoice(null)}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Fatura Detayı</h2>
              <button className="modal-close" onClick={() => setSelectedInvoice(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {detailLoading ? (
                <div className="page-loading"><Loader2 className="spinning" size={32} /></div>
              ) : (
                <>
                  <div className="fi-detail-section">
                    <h3>Kurye Bilgileri</h3>
                    <div className="fi-detail-grid">
                      <div className="fi-detail-item">
                        <span className="fi-detail-label">Kurye</span>
                        <span className="fi-detail-value">{selectedInvoice.courierName || '-'}</span>
                      </div>
                      <div className="fi-detail-item">
                        <span className="fi-detail-label">Telefon</span>
                        <span className="fi-detail-value">{selectedInvoice.courierPhone || '-'}</span>
                      </div>
                      <div className="fi-detail-item">
                        <span className="fi-detail-label">Dönem</span>
                        <span className="fi-detail-value">{selectedInvoice.period?.display || '-'}</span>
                      </div>
                      <div className="fi-detail-item">
                        <span className="fi-detail-label">Durum</span>
                        {getStatusBadge(selectedInvoice.status)}
                      </div>
                    </div>
                  </div>

                  {selectedInvoice.summary && (
                    <div className="fi-detail-section">
                      <h3>Özet</h3>
                      <div className="fi-detail-grid">
                        <div className="fi-detail-item">
                          <span className="fi-detail-label">Toplam Teslimat</span>
                          <span className="fi-detail-value">{selectedInvoice.summary.totalDeliveries ?? 0}</span>
                        </div>
                        <div className="fi-detail-item">
                          <span className="fi-detail-label">Toplam Mesafe</span>
                          <span className="fi-detail-value">{selectedInvoice.summary.totalDistanceKm?.toFixed(1) ?? 0} km</span>
                        </div>
                        <div className="fi-detail-item">
                          <span className="fi-detail-label">Toplam Kazanç</span>
                          <span className="fi-detail-value">{formatCurrency(selectedInvoice.summary.totalEarnings)}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedInvoice.invoices && selectedInvoice.invoices.length > 0 && (
                    <div className="fi-detail-section">
                      <h3>Fatura Kalemleri</h3>
                      {selectedInvoice.invoices.map((inv, idx) => (
                        <div key={idx} className="fi-invoice-item">
                          <span>{inv.invoiceNumber || `Fatura ${idx + 1}`}</span>
                          <span>{formatDate(inv.invoiceDate)}</span>
                          <span>{formatCurrency(inv.invoiceAmount)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {selectedInvoice.adminNote && (
                    <div className="fi-detail-section">
                      <h3>Admin Notu</h3>
                      <p className="fi-admin-note">{selectedInvoice.adminNote}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicesPage;
